import React from 'react';

import { Skeleton } from '../Skeletons/Skeleton';
import clsx from 'clsx';

const sizes = {
  sm: 'p-5 [&_.card-heading]:text-2xl [&_.heading-icon]:text-3xl ',
  md: 'p-5 sm:p-10 [&_.card-heading]:text-3xl [&_.heading-icon]:text-4xl ',
  'no-padding': 'p-0',
} as const;

export type CardSize = keyof typeof sizes;

interface CardProps {
  children?: React.ReactNode;
  title?: string;
  href?: string;
  external?: boolean;
  icon?: string;
  loading?: boolean;
  error?: string | null | unknown;
  errorMessage?: string;
  description?: string;
  size?: CardSize;
  skeletonCount?: number;
  className?: string;
}

export const Card = ({
  children,
  title,
  description,
  href,
  external,
  icon,
  size = 'md',
  loading,
  error,
  errorMessage = 'Something went wrong, please try refreshing the page or contact support if error persists.',
  skeletonCount,
  className,
}: CardProps) => {
  return (
    <div
      className={clsx(
        ' w-full  border dark:bg-blue-1000 dark:border-grey-700 bg-grey-200 border-grey-400 rounded-2xl',
        sizes[size],
        className
      )}
    >
      {loading ? (
        <Skeleton count={skeletonCount} />
      ) : error ? (
        <div className="flex flex-col items-center mx-auto w-full max-w-lg">
          <span className="text-7xl material-symbols-outlined">error</span>
          <p className="mt-4 text-xl text-center">{errorMessage}</p>
        </div>
      ) : (
        <>
          {title && href ? (
            <a
              href={href}
              target={external ? '_blank' : '_self'}
              className="flex gap-2 items-center text-blue-700 hover:text-blue-900 dark:text-blue-300 transition-color dark:hover:text-blue-500"
            >
              {icon && <span className="h-min heading-icon material-symbols-outlined">{icon}</span>}
              <h2 className={clsx('font-bold underline card-heading ', description ? 'mb-2' : '')}>
                {title}
              </h2>
            </a>
          ) : (
            <div>
              {title && (
                <span className="flex gap-2 items-center text-blue-1000 dark:text-blue-100">
                  {icon && (
                    <span className="h-min heading-icon material-symbols-outlined">{icon}</span>
                  )}
                  <h2 className={clsx('font-bold card-heading ', description ? 'mb-2' : '')}>
                    {title}
                  </h2>
                </span>
              )}
            </div>
          )}

          {description && (
            <p className="max-w-2xl dark:text-blue-100 text-grey-1000">{description}</p>
          )}
          <div className={clsx(size === 'no-padding' ? '' : children ? 'mt-4' : '')}>
            {children}
          </div>
        </>
      )}
    </div>
  );
};
